import { useState } from 'react';
import { Activity, Lightbulb } from 'lucide-react';
import { api } from '../../api/client';

interface Signal {
  text: string;
  createdAt: string;
}

interface SignalAnalysis {
  patterns: string[];
  pivotRisk: 'low' | 'medium' | 'high';
  suggestion: string;
  analyzedAt: string;
}

interface Props {
  startup: Record<string, any>;
  onSignalLogged: (signal: Signal, analysis: SignalAnalysis | null) => void;
}

const C = {
  bg: '#0d0d1f',
  surface: '#161627',
  border: '#2a2a3e',
  primary: '#7c6ff7',
  accent: '#00d4ff',
  green: '#22c55e',
  amber: '#f59e0b',
  danger: '#ff4466',
  text: '#f0f0ff',
  text2: '#8888aa',
  text3: '#444466',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const RISK_COLORS: Record<string, string> = { low: C.green, medium: C.amber, high: C.danger };

export default function SignalLog({ startup, onSignalLogged }: Props) {
  const [inputText, setInputText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signals, setSignals] = useState<Signal[]>(startup.signals ?? []);
  const [analysis, setAnalysis] = useState<SignalAnalysis | null>(startup.signalAnalysis ?? null);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 4000); };

  const logSignal = async () => {
    if (!inputText.trim() || isSubmitting) return;
    setIsSubmitting(true);
    setError('');
    try {
      const { data } = await api.post(`/startups/${startup.id}/log-signal`, { text: inputText.trim() });
      const newSignal = data.data.signal;
      setSignals(prev => [newSignal, ...prev]);
      if (data.data.analysis) setAnalysis(data.data.analysis);
      if (data.data.creditsAwarded > 0) {
        showToast(`First signal logged! +${data.data.creditsAwarded} credit awarded`);
      }
      onSignalLogged(newSignal, data.data.analysis);
      setInputText('');
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || 'Failed to log signal');
    } finally {
      setIsSubmitting(false);
    }
  };

  const reanalyze = async () => {
    setReanalyzing(true);
    try {
      const { data } = await api.post(`/startups/${startup.id}/analyze-signals`);
      setAnalysis(data.data.signalAnalysis);
    } catch {
      // silent fail
    } finally {
      setReanalyzing(false);
    }
  };

  return (
    <div className="card" style={{ padding: 20 }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}><Activity size={16} className="text-brand-500" /> Signal Log</h3>
      <p style={{ fontSize: 12, color: C.text2, marginBottom: 14 }}>
        What did you hear today from potential users?
      </p>

      {/* Input */}
      <textarea
        value={inputText}
        onChange={e => setInputText(e.target.value)}
        placeholder={startup.name && startup.targetSegment
          ? `e.g. Spoke with a ${startup.targetSegment} today about ${startup.name}. They said they'd pay $X/mo but worried about...`
          : "e.g. Spoke with a potential user today. They loved the concept but said they'd only pay $9, not $19. Main objection was..."}
        rows={3}
        style={{
          width: '100%', background: C.bg, border: `1px solid ${C.border}`,
          borderRadius: 8, color: C.text, fontSize: 13, padding: '10px 12px',
          outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
        }}
      />
      {error && <p style={{ color: C.danger, fontSize: 11, marginTop: 4 }}>{error}</p>}
      <button
        onClick={logSignal}
        disabled={!inputText.trim() || isSubmitting}
        style={{
          marginTop: 8, width: '100%',
          background: `linear-gradient(135deg, ${C.primary}, ${C.accent})`,
          border: 'none', borderRadius: 8, color: '#fff',
          fontSize: 13, fontWeight: 700, padding: '9px 0',
          cursor: (!inputText.trim() || isSubmitting) ? 'not-allowed' : 'pointer',
          opacity: (!inputText.trim() || isSubmitting) ? 0.5 : 1,
        }}
      >
        {isSubmitting ? 'Logging...' : 'Log Signal →'}
      </button>

      {/* Toast */}
      {toast && (
        <div style={{
          marginTop: 8, background: `${C.green}15`, border: `1px solid ${C.green}40`,
          borderRadius: 8, padding: '8px 12px', fontSize: 12, color: C.green, textAlign: 'center',
        }}>{toast}</div>
      )}

      {/* Signals feed */}
      {signals.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: C.text3, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>
            Signals ({signals.length})
          </p>
          <div style={{ maxHeight: 200, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[...signals].reverse().map((s, i) => (
              <div key={i} style={{
                padding: '8px 10px', background: C.bg,
                borderRadius: 8, border: `1px solid ${C.border}`,
              }}>
                <p style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>{s.text}</p>
                <p style={{ fontSize: 10, color: C.text3, marginTop: 4 }}>{timeAgo(s.createdAt)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Pattern Analysis */}
      {analysis && signals.length >= 3 && (
        <div style={{ marginTop: 16, padding: 14, background: `${C.primary}08`, borderRadius: 10, border: `1px solid ${C.primary}20` }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: C.primary, marginBottom: 8 }}>Patterns detected</p>
          {(analysis.patterns ?? []).map((p, i) => (
            <p key={i} style={{ fontSize: 12, color: C.text2, marginBottom: 3 }}>→ {p}</p>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 11, color: C.text3 }}>Pivot Risk:</span>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
              background: `${RISK_COLORS[analysis.pivotRisk] ?? C.amber}20`,
              color: RISK_COLORS[analysis.pivotRisk] ?? C.amber,
              textTransform: 'uppercase',
            }}>{analysis.pivotRisk}</span>
          </div>
          {analysis.suggestion && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
              <Lightbulb size={13} />
              <p style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>{analysis.suggestion}</p>
            </div>
          )}
          <button onClick={reanalyze} disabled={reanalyzing} style={{
            marginTop: 10, background: 'transparent', border: `1px solid ${C.primary}30`,
            borderRadius: 6, color: C.primary, fontSize: 11, padding: '5px 12px', cursor: 'pointer',
            opacity: reanalyzing ? 0.5 : 1,
          }}>
            {reanalyzing ? 'Analyzing...' : 'Re-analyze →'}
          </button>
        </div>
      )}
    </div>
  );
}
